import * as React from 'react';
import { Button } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../store';
import { trainingActionCreators } from '../../store/Training';
import { ChoiceValue, RecallValue, CardOption } from '../../types/TrainingTypes';
import { recallActionCreators } from '../../store/Recall';
import RecallAssess from './RecallAssess';
import ChoiceOptions from './ChoiceOptions';
import Example from './Example';
import PhraseColoring from '../card/PhraseColoring';
import getBorderColor from './borderColor';

export default function Recall() {
    const trainingState = useAppSelector(state => state.training);
    const state = useAppSelector(state => state.recall);
    const dispatch = useAppDispatch();
    const [isTimerActive, setIsTimerActive] = React.useState(false);
    let borderColor = getBorderColor(trainingState.card?.resultInfo?.reviews);

    function showSolution() {
        dispatch(recallActionCreators.showSolution());
    }

    function assess(assessmentValue: RecallValue) {
        dispatch(trainingActionCreators.recallAssess(assessmentValue));
        dispatch(trainingActionCreators.nextCard());
    };

    function showOptions() {
        dispatch(recallActionCreators.showOptions());
    }

    function nextCard() {
        dispatch(trainingActionCreators.nextCard());
    }

    function chooseOption(option: CardOption | undefined, isLong: boolean) {
        if (!isTimerActive) {
            const isCorrect = option?.content === state.solution;
            if (trainingState.isSolutionShown) {
                if (option?.content === state.solution || !trainingState.isServicePanelEnabled)
                    nextCard();
            }
            else {
                if (isCorrect && !trainingState.isServicePanelEnabled) {
                    setIsTimerActive(true);
                    setTimeout(() => {
                        setIsTimerActive(false);
                        nextCard();
                    }, 300);
                }
                dispatch(recallActionCreators.chooseOption(option));

                const assessmentValue = isCorrect
                    ? isLong
                        ? ChoiceValue.Know
                        : ChoiceValue.Correct
                    : isLong
                        ? ChoiceValue.Decrease
                        : ChoiceValue.Incorrect;
                const time = new Date().getTime() - new Date(state.shownTime!).getTime();

                let difficulty = Math.min(20, (6 + state.task.length));
                let correctOptionPassed = false;
                for (let i = 0; i < state.options.length; i++) {
                    difficulty += (Math.min(20, (6 + state.options[i].content.length)) * (correctOptionPassed ? 0.6 : 1));
                    correctOptionPassed ||= (state.options[i].content === option?.content);
                }
                dispatch(trainingActionCreators.choiceAssess(assessmentValue, time, Math.round(difficulty)));
            }
        }
    }

    return (
        <>
            <div className="flex-grow-1 w-100 width-limit d-flex flex-column text-break">

                <div className="training-box-tasks">
                    <div className="training-box"
                        style={{ borderColor: borderColor }}>
                        <div>
                            <span className="task">
                                <PhraseColoring phrase={state.task} phraseColoring={state.taskColoring} />
                            </span>
                            {state.taskAux &&
                                <>
                                    <br />
                                    <span className="text-secondary"> {state.taskAux}</span>
                                </>
                            }
                            {state.taskForms &&
                                <>
                                    <br />
                                    <span className="text-secondary"> {state.taskForms}</span>
                                </>
                            }
                        </div>
                    </div>

                    {state.example &&
                        <div className="example training-box mt-2">
                            <Example example={state.example} phrase={trainingState.card!.phrase} language={trainingState?.card?.modeInfo?.wiktionaryLanguage} />
                        </div>
                    }
                </div>

                {!state.isOptionsShown ?
                    !trainingState.isSolutionShown ?
                        <>
                            <Button variant="light"
                                size="lg"
                                className="btn-tall mt-2"
                                disabled={!state.options?.length}
                                onClick={showOptions}>
                                Show options
                            </Button>

                            <Button variant="light"
                                size="lg"
                                className="btn-tall mt-2"
                                onClick={showSolution}>
                                Show meaning
                            </Button>
                        </>
                        :
                        <>
                            <div className="training-box mt-2">
                                <div className="solution">
                                    <span style={{ whiteSpace: 'pre-wrap' }}>{state.maskedSolution}</span>
                                    {state.maskedSolutionAux &&
                                        <span className="text-secondary"> {state.maskedSolutionAux}</span>
                                    }
                                    {state.maskedSolutionForms &&
                                        <span className="text-secondary"> {state.maskedSolutionForms}</span>
                                    }
                                </div>
                            </div>

                            <div className="mt-2">
                                <RecallAssess assess={assess} />
                            </div>
                        </>
                    : <div className="mt-2">
                        <ChoiceOptions options={state.options}
                            backgrounds={state.backgrounds}
                            chooseOption={chooseOption}
                        />

                        {state.wrongOption &&
                            <div className="fs-5 mt-2"><span className="red">{state.wrongOption.content}</span> - <span className="fw-bold">{state.wrongOption.wrongOption}</span></div>
                        }
                    </div>
                }

                {state.similar && ((!state.isOptionsShown && trainingState.isSolutionShown) || state.wrongOption) &&
                    <ul className="list-unstyled mt-2 mb-0 fs-5">
                        {state.similar.map(x =>
                            <li key={x.id}><span className="fw-bold">
                                {x.phrase}</span>{x.dictionaryName ? ` (${x.dictionaryName})` : ``} - {x.meaning}
                            </li>
                        )}
                    </ul>
                }
            </div>
        </>
    );
}
